import { useEffect, useState } from "react";

import { updateConsent } from "@/common/google-ads";
import { cn } from "@/common/utils";
import gridThemeCss from "@/landing/styles/grid-theme.css?url";
import { pillClassName } from "@/landing/system/controls";

const STORAGE_KEY = "cookieConsent";
const PREFS_KEY = "cookiePreferences";
const REOPEN_EVENT = "reopen-cookie-consent";

type ConsentPreferences = {
  analytics: boolean;
  marketing: boolean;
};

function readStoredPreferences(): ConsentPreferences | null {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ConsentPreferences>;
    return {
      analytics: Boolean(parsed.analytics),
      marketing: Boolean(parsed.marketing),
    };
  } catch {
    return null;
  }
}

export function CookieConsent() {
  const [mounted, setMounted] = useState(false);
  // Grid tokens: the landing already loads them; elsewhere (the app, the Wonda
  // Twin root) the banner brings its own sheet. Loading it twice would re-order
  // the landing's cascade, so only when missing.
  const [needsGridCss, setNeedsGridCss] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [showManage, setShowManage] = useState(false);
  const [preferences, setPreferences] = useState<ConsentPreferences>({
    analytics: false,
    marketing: false,
  });

  // Initial mount: restore saved consent or show banner.
  useEffect(() => {
    setMounted(true);
    setNeedsGridCss(
      !document.querySelector(`link[rel="stylesheet"][href="${gridThemeCss}"]`),
    );
    // localStorage can throw in sandboxed iframes / restrictive CSPs — treat
    // any failure as "no consent recorded" and show the banner.
    let hasConsent: string | null = null;
    try {
      hasConsent = localStorage.getItem(STORAGE_KEY);
    } catch {
      setShowBanner(true);
      return;
    }
    if (!hasConsent) {
      setShowBanner(true);
      return;
    }
    const stored = readStoredPreferences();
    if (stored) {
      setPreferences(stored);
      updateConsent(stored);
    }
  }, []);

  // Allow other parts of the app to reopen the banner (e.g. footer link).
  useEffect(() => {
    const handler = () => {
      const stored = readStoredPreferences();
      if (stored) setPreferences(stored);
      setShowManage(true);
      setShowBanner(true);
    };
    window.addEventListener(REOPEN_EVENT, handler);
    return () => window.removeEventListener(REOPEN_EVENT, handler);
  }, []);

  const persist = (
    prefs: ConsentPreferences,
    status: "accepted" | "managed" | "rejected",
  ) => {
    // localStorage may throw under restrictive policies — update gtag consent
    // regardless so the user's choice still applies for this session.
    try {
      localStorage.setItem(STORAGE_KEY, status);
      localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
    } catch {
      // ignore — consent will simply re-prompt next visit
    }
    updateConsent(prefs);
  };

  const acceptAll = () => {
    const prefs = { analytics: true, marketing: true };
    setPreferences(prefs);
    persist(prefs, "accepted");
    setShowBanner(false);
    setShowManage(false);
  };

  const rejectAll = () => {
    const prefs = { analytics: false, marketing: false };
    setPreferences(prefs);
    persist(prefs, "rejected");
    setShowBanner(false);
    setShowManage(false);
  };

  const savePreferences = () => {
    persist(preferences, "managed");
    setShowBanner(false);
    setShowManage(false);
  };

  if (!mounted || !showBanner) return null;

  return (
    // Mounted globally (__root), outside any page's theme scope, so it carries
    // .grid-theme itself; the inline background keeps that class from painting
    // the full-width strip.
    <div
      className="grid-theme pointer-events-none fixed inset-x-0 bottom-0 z-50 flex justify-center p-4 sm:p-(--g-margin)"
      style={{ background: "transparent" }}
    >
      {needsGridCss && <link rel="stylesheet" href={gridThemeCss} />}
      <section
        aria-label="Cookie preferences"
        className="g-round g-glass-raised pointer-events-auto w-full p-5 sm:w-[400px]"
        style={{ background: "var(--g-glass-solid)" }}
      >
        {!showManage ? (
          <>
            <h2 className="m-0 text-[15px] font-medium text-(--g-ink)">
              Cookies
            </h2>
            <p className="mt-1.5 mb-0 text-[13.5px] leading-[1.5] text-(--g-ink-2)">
              We use cookies to run the site, measure traffic and personalize
              ads.
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setShowManage(true)}
                className={LINK_BUTTON}
              >
                Manage
              </button>
              <span className="flex-1" />
              <button
                type="button"
                onClick={rejectAll}
                className={cn(pillClassName("glass"), SMALL_PILL)}
              >
                Reject all
              </button>
              <button
                type="button"
                onClick={acceptAll}
                className={cn(pillClassName("ink"), SMALL_PILL)}
              >
                Accept all
              </button>
            </div>
          </>
        ) : (
          <>
            <h2 className="m-0 text-[15px] font-medium text-(--g-ink)">
              Cookie preferences
            </h2>
            <div className="mt-2">
              <CookiePreferenceRow
                title="Essential"
                description="Required for the site to work. Always on."
                alwaysOn
              />
              <CookiePreferenceRow
                title="Analytics"
                description="Help us understand how visitors use the site."
                enabled={preferences.analytics}
                onToggle={() =>
                  setPreferences((prev) => ({
                    ...prev,
                    analytics: !prev.analytics,
                  }))
                }
              />
              <CookiePreferenceRow
                title="Marketing"
                description="Used to deliver personalized ads and measure campaigns."
                enabled={preferences.marketing}
                onToggle={() =>
                  setPreferences((prev) => ({
                    ...prev,
                    marketing: !prev.marketing,
                  }))
                }
              />
            </div>
            <div className="mt-4 flex items-center gap-2">
              <button
                type="button"
                onClick={() => setShowManage(false)}
                className={LINK_BUTTON}
              >
                Back
              </button>
              <span className="flex-1" />
              <button
                type="button"
                onClick={savePreferences}
                className={cn(pillClassName("ink"), SMALL_PILL)}
              >
                Save preferences
              </button>
            </div>
          </>
        )}
      </section>
    </div>
  );
}

const SMALL_PILL = "h-9 px-4 text-[14px]";
const LINK_BUTTON =
  "cursor-pointer border-0 bg-transparent p-0 text-[14px] text-(--g-ink) underline decoration-(--g-rule) decoration-1 underline-offset-[0.22em] transition-colors hover:decoration-current";

type PreferenceRowProps = {
  title: string;
  description: string;
  enabled?: boolean;
  onToggle?: () => void;
  alwaysOn?: boolean;
};

function CookiePreferenceRow({
  title,
  description,
  enabled,
  onToggle,
  alwaysOn,
}: PreferenceRowProps) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-(--g-rule-soft) py-3 last:border-b-0">
      <div className="min-w-0">
        <h3 className="m-0 text-[14px] font-medium text-(--g-ink)">{title}</h3>
        <p className="mt-0.5 mb-0 text-[13px] leading-[1.45] text-(--g-ink-2)">
          {description}
        </p>
      </div>
      <div className="flex-none pt-0.5">
        {alwaysOn ? (
          <span className="font-mono text-[11.5px] text-(--g-ink-3)">
            Always on
          </span>
        ) : (
          <ConsentToggle
            enabled={Boolean(enabled)}
            onToggle={onToggle}
            label={title}
          />
        )}
      </div>
    </div>
  );
}

function ConsentToggle({
  enabled,
  onToggle,
  label,
}: {
  enabled: boolean;
  onToggle?: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      aria-label={label}
      onClick={onToggle}
      className={cn(
        "relative inline-flex h-5 w-9 cursor-pointer items-center rounded-full border-0 transition-colors",
        enabled ? "bg-(--g-ink)" : "bg-(--g-rule)",
      )}
    >
      <span
        className={cn(
          "inline-block size-3.5 rounded-full bg-(--g-lit) transition-transform",
          enabled ? "translate-x-[19px]" : "translate-x-[3px]",
        )}
      />
    </button>
  );
}
